/**
 * `useDisplayNames(upns)` (13-25): UPN → display name for the record timeline, through one User Information List
 * read (`services/displayNames`). Until it lands, or when a name isn't found, the caller shows the UPN. The
 * signed-in person's own name comes from their sign-in, with no read.
 */

import { useEffect, useMemo, useState } from 'react';
import { useActionRuntime } from './useActionRuntime';
import { knownNames, readDisplayNames } from '../services/displayNames';
import type { Actor } from '../types/actionRegister';

export function useDisplayNames(upns: readonly string[], actor: Actor | null): ReadonlyMap<string, string> {
  const { io } = useActionRuntime();
  const key = [...new Set(upns.map((u) => u.trim().toLowerCase()))].sort().join(',');
  const [read, setRead] = useState<{ key: string; names: Map<string, string> } | null>(null);

  useEffect(() => {
    if (!io || key === '') return;
    let cancelled = false;
    void readDisplayNames(io, key.split(',')).then((names) => {
      if (!cancelled) setRead({ key, names });
    });
    return () => {
      cancelled = true;
    };
  }, [io, key]);

  const selfUpn = actor?.upn ?? '';
  const selfName = actor?.displayName ?? '';
  return useMemo(() => {
    const names = new Map(read && read.key === key ? read.names : knownNames(key ? key.split(',') : []));
    if (selfUpn && selfName) names.set(selfUpn.trim().toLowerCase(), selfName);
    return names;
  }, [read, key, selfUpn, selfName]);
}
